import * as api from '../../api/brands';
import * as mutationTypes from './mutationTypes';
import { changeObjectKeysToCamelCase, changeObjectKeysToSnakeCase, getErrorMessage } from '../../helpers/functions';
import { changeEmptyValuesToNull, handleBrandCreationErrors } from '../../helpers/brands/functions';
import {getAccountCatalog, getPaymentMethods} from "../../api/billing";
import {
  buildBillingObject,
  getBrandCatalogRatePlanId,
  getDefaultPaymentMethod
} from "../../helpers/billing/functions";

const actions = {
  async fetchBrands({ commit, rootState }) {
    commit(mutationTypes.FETCH_BRANDS);
    const credentials = rootState.credentials;
    try {
      const { data: brandsResponse } = await api.getBrands(credentials);
      const brands = brandsResponse.map(brandResponse => {
        const brand  = changeObjectKeysToCamelCase(brandResponse.vonage_api_brand);
        brand.id = brandResponse.id;
        brand.campaignCount = brandResponse.campaigns_count;
        return brand;
      });
      const sortedBrands = brands.sort((a, b) => (a.brandId > b.brandId) ? 1 : -1);
      commit(mutationTypes.DONE_FETCHING_BRANDS, { brands: sortedBrands });
    } catch (e) {
      commit(mutationTypes.ERROR_FETCHING_BRANDS, { error: getErrorMessage(e) });
    }
  },

  async fetchBrand({ commit, rootState, state }, { brandId }) {
    commit(mutationTypes.FETCH_BRAND);
    const credentials = rootState.credentials;
    const id = state.brands.find(brand => brand.brandId === brandId).id;
    try {
      const { data: brandResponse } = await api.getBrand(id, credentials);
      const brand = brandResponse.vonage_api_brand;
      brand.id = brandResponse.id;
      brand.campaignCount = brandResponse.campaigns_count;
      commit(mutationTypes.DONE_FETCHING_BRAND, { brand: changeObjectKeysToCamelCase(brand) });
    } catch (e) {
      commit(mutationTypes.ERROR_FETCHING_BRAND, { error: getErrorMessage(e) });
    }
  },

  async fetchBrandFeedback({ commit, rootState, state }, { brandId, id }) {
    commit(mutationTypes.FETCH_BRAND_FEEDBACK);
    const credentials = rootState.credentials;
    const serverId = id || state.brands.find(brand => brand.brandId === brandId).id;
    try {
      const { data: feedback } = await api.getFeedback(serverId, credentials);
      commit(mutationTypes.DONE_FETCHING_BRAND_FEEDBACK, { feedback });
    } catch (e) {
      commit(mutationTypes.ERROR_FETCHING_BRAND_FEEDBACK, {
        error: 'brands.feedback.errorMessage',
      });
    }
  },

  async createBrand({ commit, rootState }, brand) {
    commit(mutationTypes.CREATE_BRAND);
    const credentials = rootState.credentials;

    try {
      const paymentMethods = await getPaymentMethods(rootState.credentials);
      const paymentMethodId = getDefaultPaymentMethod(paymentMethods.data).id;
      const accountCatalog = await getAccountCatalog(rootState.credentials);
      const brandCatalogRatePlanId = getBrandCatalogRatePlanId(accountCatalog.data);
      const isMultiLocationAccount = rootState.accountDetails.Locations.length > 1;
      const { data } = await api.createBrand(credentials, { brand_request: changeObjectKeysToSnakeCase(changeEmptyValuesToNull({ ...brand })),
        billing_request:  buildBillingObject(rootState.accountDetails, rootState.credentials.accountId, isMultiLocationAccount, brandCatalogRatePlanId.productRatePlanId, paymentMethodId)} );
      commit(mutationTypes.DONE_CREATING_BRAND, { brand: changeObjectKeysToCamelCase(data) });
    } catch (e) {
      const errorTitle = getErrorMessage(e, 'title');
      if (errorTitle === 'Conflict') {
        commit(mutationTypes.ERROR_CREATING_BRAND, { error: getErrorMessage(e, 'detail') });
        return;
      }
      commit(mutationTypes.ERROR_CREATING_BRAND, { error: handleBrandCreationErrors(getErrorMessage(e)) });
    }
  },

  async updateAndVerifyBrand({ commit }, brand) {
    commit(mutationTypes.UPDATE_AND_REVET_BRAND);
    let data;
    try {
      ({ data } = await api.updateAndVerifyBrand(changeObjectKeysToSnakeCase(changeEmptyValuesToNull({ ...brand }))));
      commit(mutationTypes.DONE_UPDATING_AND_REVETIING_BRAND, { brand: changeObjectKeysToCamelCase(data) });
    } catch (e) {
      commit(mutationTypes.ERROR_UPDATING_AND_REVETTING_BRAND, {
        error: handleBrandCreationErrors(getErrorMessage(e)),
      });
    }
  },

  async updateBrand({ commit }, brand) {
    commit(mutationTypes.UPDATE_BRAND);
    let data;
    try {
      ({ data } = await api.updateBrand(changeObjectKeysToSnakeCase(changeEmptyValuesToNull({ ...brand }))));
      commit(mutationTypes.DONE_UPDATING_BRAND, { brand: changeObjectKeysToCamelCase(data) });
    } catch (e) {
      commit(mutationTypes.ERROR_UPDATING_BRAND, { error: handleBrandCreationErrors(getErrorMessage(e)) });
    }
  },

  async deactivateBrand({ commit, rootState }, { brand, stayOnSamePage }) {
    commit(mutationTypes.DEACTIVATE_BRAND);
    const credentials = rootState.credentials;

    try {
      await api.deactivateBrand(credentials, brand.id);
      if (stayOnSamePage) {
        const { data: brandsResponse } = await api.getBrands(credentials);
        const brands = brandsResponse.map(brandResponse => {
          const brand  = changeObjectKeysToCamelCase(brandResponse.vonage_api_brand);
          brand.id = brandResponse.id;
          return brand;
        });
        const sortedBrands = brands.sort((a, b) => (a.brandId > b.brandId) ? 1 : -1);
        commit(mutationTypes.DONE_FETCHING_BRANDS, { brands: sortedBrands });
      }
      commit(mutationTypes.DONE_DEACTIVATING_BRAND, { brand });
    } catch (e) {
      commit(mutationTypes.ERROR_DEACTIVATING_BRAND, { error: getErrorMessage(e) });
    }
  },

  removeLastCreatedBrand({ commit }) {
    commit(mutationTypes.REMOVE_LAST_CREATED_BRAND);
  },
  removeLastDeactivatedBrand({ commit }) {
    commit(mutationTypes.REMOVE_LAST_DEACTIVATED_BRAND);
  },
  removeCreationErrors({ commit }, key) {
    commit(mutationTypes.REMOVE_CREATION_ERRORS, { key });
  },
  removeDeactivationErrors({ commit }, key) {
    commit(mutationTypes.REMOVE_DEACTIVATION_ERROR, { key });
  },
  removeUpdateErrors({ commit }, key) {
    commit(mutationTypes.REMOVE_UPDATE_ERROR, { key });
  },
  changeBrandListView({ commit }, isListView) {
    commit(mutationTypes.CHANGE_BRANDS_LIST_VIEW, { isListView });
  },
};

export default actions;
